<script setup lang="ts">
import axios from 'axios'

import type { CampaignMessageCategory, CampaignMessageTemplate } from '../../types/types'

const { user } = useLocalStorage()

const isLoaded = ref(false)
const templatesIsLoading = ref(false)
const { t } = useI18n()
const categories = ref<CampaignMessageCategory[]>([])
const templates = ref<CampaignMessageTemplate[]>([])
const activeTemplate = ref<CampaignMessageTemplate>(null)
const categoriesRef = ref<HTMLElement | null>(null)

const emit = defineEmits<{
  (e: 'update:message', value: string): void
}>()

const fetchCategories = async () => {
  try {
    const { data } = await axios.get('/campaign-templates/categories', {
      params: {
        merchantId: user.value.merchant.id
      }
    })
    categories.value = data.data
  } finally {
    isLoaded.value = true
  }
}

fetchCategories()

const fetchTemplates = async (categoryId: number) => {
  templatesIsLoading.value = true
  try {
    const { data } = await axios.get('/campaign-templates', {
      params: {
        campaignTemplateCategoryId: categoryId
      }
    })
    templates.value = data.data
  } finally {
    templatesIsLoading.value = false
  }
}
const handleCategoryClick = (categoryId: number) => {
  fetchTemplates(categoryId)
  handleScroll()
}
const handleScroll = () => {
  // Scroll to messages templates on category click
  const element = document.getElementsByTagName('main')[0]
  if (categoriesRef.value) {
    const categoriesRefTop = categoriesRef.value.getBoundingClientRect().top

    if (categoriesRefTop > 598) {
      element.scroll({
        top: categoriesRefTop - 350,
        left: 0,
        behavior: 'smooth'
      })
    }
  }
}
const sendTemplate = (template: CampaignMessageTemplate) => {
  // Remove active state on previous active element
  if (activeTemplate.value) {
    activeTemplate.value.isActive = false
  }
  template.isActive = true
  activeTemplate.value = templates.value.filter((template) => template.isActive)[0]
  emit('update:message', template.content)
}
</script>

<template>
  <div class="overflow-hidden rounded-md border bg-white">
    <div class="p-6">
      <div class="py-6 text-center">
        <h2 class="text-lg font-medium text-foreground">
          {{ t(`pages.campaign.campaignCreatePage.templates.header.title`) }}
        </h2>
        <h3 class="text-lg text-muted-foreground">
          {{ t(`pages.campaign.campaignCreatePage.templates.header.description`) }}
        </h3>
      </div>
      <div class="flow-root">
        <div ref="categoriesRef" class="mb-6 flex flex-wrap gap-4">
          <div
            v-for="(category, categorieIndex) in categories"
            :key="categorieIndex"
            class="w-2/5 grow transform cursor-pointer rounded-lg p-4 text-center transition-all hover:scale-105 lg:w-1/6"
            :style="`background-color: ${category.color};`"
            @click="handleCategoryClick(category.id)"
          >
            <p class="text-2xl">
              {{ category.emoji }}
            </p>
            <p
              class="mt-3 flex h-10 items-center justify-center rounded-full bg-white px-2 py-0.5 font-semibold text-foreground"
              style="font-size: 11px; line-height: 13px"
            >
              {{ category.name }}
            </p>
          </div>
        </div>
        <div v-if="!isLoaded || templatesIsLoading" class="flex w-full items-center justify-center p-6 text-sm">
          <svg
            class="mr-3 h-5 w-5 animate-spin text-muted-foreground"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path
              class="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span class="text-muted-foreground">
            {{ t(`pages.campaign.campaignCreatePage.templates.messages.show`) }}
          </span>
        </div>
        <div v-else ref="templatesRef" class="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 2xl:grid-cols-3">
          <div
            v-for="template in templates"
            :key="template.id"
            class="col-span-1 mb-2 flex flex-col justify-between rounded-lg"
          >
            <div class="mb-3 flex items-center">
              <p class="rounded-full px-2 py-1 text-sm" :style="`background-color: ${template.category.color};`">
                {{ template.category.emoji }}
              </p>
              <p class="rounded-full px-2 py-0.5 text-sm font-medium text-foreground">
                {{ template.title }}
              </p>
            </div>
            <div
              class="flex h-44 cursor-pointer flex-col justify-between rounded-sm border px-2 py-5 text-foreground-secondary transition xl:h-36 2xl:h-44"
              :class="template.isActive ? '' : 'hover:bg-primary/5 hover:text-muted-foreground hover:border-primary'"
              @click="sendTemplate(template)"
            >
              <p class="text-sm">
                {{ template.content }}
              </p>
              <div v-if="template.isActive" class="mt-4 flex items-center justify-end text-xs font-bold text-primary">
                <span>{{ t(`pages.campaign.campaignCreatePage.templates.messages.used`) }}</span>
                <BaseIcon name="HeroClipboardDocumentCheckIconOutline" class="ml-2 h-4 w-4" />
              </div>
              <div v-else class="mt-4 flex items-center justify-end text-xs font-bold">
                <span>{{ t(`pages.campaign.campaignCreatePage.templates.messages.useModel`) }}</span>
                <BaseIcon name="HeroClipboardDocumentIconOutline" class="ml-2 h-4 w-4" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
